import React, { useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { Switch } from "../ui/switch";
import { api, errorMessage } from "../../lib/api";

/** One add-on x one region (or US when region.id === "us"): price + estimated flag. */
export default function RegionalAddonRow({ addon, region, adminKey, onSaved }) {
  const us = region.id === "us";
  const local = us ? { price: addon.price, estimated: false } : addon.regional?.[region.id];
  const [price, setPrice] = useState(local?.price ?? "");
  const [estimated, setEstimated] = useState(!!local?.estimated);
  const [busy, setBusy] = useState(false);
  const dirty = String(price) !== String(local?.price ?? "") || (!us && estimated !== !!local?.estimated);
  const suggested = !us && addon.price ? +(addon.price * (region.factor || 1)).toFixed(2) : null;

  const save = async () => {
    const p = price === "" ? null : Number(price);
    if (p !== null && Number.isNaN(p)) return toast.error("Enter a valid price");
    setBusy(true);
    try {
      const body = us ? (p === null ? { clear_price: true } : { price: p }) : { regional: { ...(addon.regional || {}), [region.id]: { price: p, estimated } } };
      const updated = await api.admin.patchAddon(adminKey, addon.name, body);
      onSaved(updated);
      toast.success(`${addon.name} · ${region.label} saved`);
    } catch (err) {
      toast.error("Update failed", { description: errorMessage(err) });
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  return (
    <tr className="feature-row" data-testid={`regional-addon-${addon.name}`}>
      <td>
        <div className="plan-name">{addon.name}</div>
        <div className="plan-full">{addon.unit || "user/month"}{addon.note ? ` · ${addon.note}` : ""}</div>
      </td>
      <td className="mono muted small">{addon.price != null ? `$${addon.price}` : "—"}</td>
      <td>
        <input className="price-input" value={price} onChange={(e) => setPrice(e.target.value)} placeholder={suggested != null ? String(suggested) : ""} aria-label={`${region.label} price`} data-testid={`regional-addon-price-${addon.name}`} />
      </td>
      <td>
        {!us && (
          <label className="filter-item">
            <Switch checked={estimated} disabled={busy} onCheckedChange={setEstimated} aria-label="Estimated price" data-testid={`regional-addon-estimated-${addon.name}`} />
            <span className="muted small">{estimated ? "≈ estimated" : "official"}</span>
          </label>
        )}
      </td>
      <td>
        <div className="admin-actions" style={{ margin: 0, gap: 6 }}>
          {suggested != null && !local && (
            <button type="button" className="tb-btn sm ghost" onClick={() => (setPrice(suggested), setEstimated(true))} data-testid={`regional-addon-suggest-${addon.name}`}>
              Use USD x {region.factor}
            </button>
          )}
          <button type="button" className="tb-btn sm" disabled={!dirty || busy} onClick={save} data-testid={`regional-addon-save-${addon.name}`}>
            <Check size={13} /> Save
          </button>
        </div>
      </td>
    </tr>
  );
}
